import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import {
  Container,
  Row,
  Col,
  Card,
  Badge,
  Table,
  Button,
  ButtonGroup,
  Image,
  ProgressBar,
  Alert,
  Spinner,
  Tabs,
  Tab,
  Modal,
  Form,
  ListGroup,
} from "react-bootstrap";
import {
  FaEdit,
  FaTrash,
  FaCheck,
  FaTimes,
  FaCalendar,
  FaLocationArrow,
  FaBatteryFull,
  FaTachometerAlt,
  FaCar,
  FaTag,
  FaUser,
  FaCogs,
  FaShieldAlt,
  FaWrench,
  FaFileAlt,
  FaMoneyBillWave,
  FaGavel,
  FaIdCard,
  FaIndustry,
} from "react-icons/fa";
import "../../styles/vehicleForm.css";
import moment from "moment";
import axios from "axios";
import { Baseurl } from "../../baseurl";

const VehicleDetailsPage = () => {
  const { id } = useParams();
  const [vehicle, setVehicle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [activeTab, setActiveTab] = useState("details");
  const [allBidder, setAllBidder] = useState([]);
  const [allBidderCount, setAllBidderCount] = useState(0);

  useEffect(() => {
    fetchVehicleDetails();
  }, [id]);

  useEffect(() => {
    if (vehicle) {
      getAllBidderDetails();
    }
  }, [vehicle]);

  const fetchVehicleDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetch(`${Baseurl}/api/vehicles/${id}`);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const resp = await response.json();
      setVehicle(resp.data);
    } catch (err) {
      setError(err.message);
      console.error("Error fetching vehicle details:", err);
    } finally {
      setLoading(false);
    }
  };

  const config = {
    headers: {
      "Content-Type": "application/json",
    },
  };

  const getAllBidderDetails = async () => {
    try {
      const url = `${Baseurl}/api/bids/vehicle/${id}`;
      const resp = await axios.get(url);
      if (resp.data) {
        setAllBidder(resp.data.data || []);
        setAllBidderCount(resp.data.count || 0);
      }
    } catch (err) {
      console.error("Error fetching bidder details:", err);
    }
  };

  const handleUpdateStatus = async (newStatus) => {
    try {
      const url = `${Baseurl}/api/vehicles/${id}`;
      const resp = await axios.put(
        url,
        { "auction.status": newStatus },
        config
      );
      if (!resp.data) throw new Error("Failed to update status");

      const updatedVehicle = resp.data.data;
      setVehicle(updatedVehicle);
      setShowStatusModal(false);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDeleteVehicle = async () => {
    try {
      const response = await fetch(`${Baseurl}/api/vehicles/${id}`, {
        method: "DELETE",
      });

      if (!response.ok) throw new Error("Failed to delete vehicle");

      // Redirect to vehicles list or show success message
      window.location.href = "/admin/vehicles";
    } catch (err) {
      setError(err.message);
    }
  };

  const formatPrice = (price) => {
    if (!price) return "N/A";
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(price);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return moment(dateString).format("LLL");
  };

  const getStatusBadgeVariant = (status) => {
    if (!status) return "secondary";
    
    const variants = {
      Live: "success",
      Upcoming: "warning",
      Ended: "secondary",
      Sold: "info",
      Unsold: "danger",
      Draft: "secondary",
    };
    return variants[status] || "secondary";
  };

  const getConditionColor = (condition) => {
    switch (condition) {
      case "Brand New":
        return "#17a2b8";
      case "Excellent":
        return "#28a745";
      case "Good":
        return "#20c997";
      case "Fair":
        return "#ffc107";
      case "Needs Repair":
        return "#dc3545";
      default:
        return "#6c757d";
    }
  };

  if (loading) {
    return (
      <Container
        className="d-flex justify-content-center align-items-center"
        style={{ height: "80vh" }}
      >
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <Alert variant="danger">
          <Alert.Heading>Error Loading Vehicle Details</Alert.Heading>
          <p>{error}</p>
          <Button variant="outline-danger" onClick={fetchVehicleDetails}>
            Retry
          </Button>
        </Alert>
      </Container>
    );
  }

  if (!vehicle) {
    return (
      <Container>
        <Alert variant="warning">Vehicle not found</Alert>
      </Container>
    );
  }

  return (
    <Container fluid className="vehicle-details-page py-4">
      {/* Header Section */}
      <Row className="mb-4">
        <Col>
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h1 className="h2 mb-1">{vehicle.title}</h1>
              <div className="text-muted">
                <span className="me-3">
                  <FaCar className="me-1" /> {vehicle.brand} {vehicle.model} •{" "}
                  {vehicle.variant}
                </span>
                <span>
                  <FaCalendar className="me-1" /> {vehicle.manufacturing?.year}
                </span>
              </div>
            </div>
            <ButtonGroup>
              <Button
                variant="outline-primary"
                onClick={() => setShowEditModal(true)}
              >
                <FaEdit /> Edit
              </Button>
              <Button
                variant="outline-warning"
                onClick={() => setShowStatusModal(true)}
              >
                Update Status
              </Button>
              <Button
                variant="outline-danger"
                onClick={() => setShowDeleteModal(true)}
              >
                <FaTrash /> Delete
              </Button>
            </ButtonGroup>
          </div>
        </Col>
      </Row>

      {/* Status Badge */}
      <Row className="mb-4">
        <Col>
          <Badge
            bg={getStatusBadgeVariant(vehicle.auction?.status)}
            className="fs-6 px-3 py-2 me-2"
          >
            {vehicle.auction?.status || "Draft"}
          </Badge>
          <Badge bg="secondary" className="fs-6 px-3 py-2 me-2">
            {vehicle.identifiers?.bodyType || "N/A"}
          </Badge>
          <Badge bg="info" className="fs-6 px-3 py-2 me-2">
            {vehicle.specifications?.category || "N/A"}
          </Badge>
          {vehicle.flags?.isFeatured && (
            <Badge bg="warning" className="fs-6 px-3 py-2 me-2">
              <FaTag className="me-1" /> Featured
            </Badge>
          )}
          {vehicle.flags?.isVerified ? (
            <Badge bg="success" className="fs-6 px-3 py-2 me-2">
              <FaCheck className="me-1" /> Verified
            </Badge>
          ) : (
            <Badge bg="secondary" className="fs-6 px-3 py-2 me-2">
              <FaTimes className="me-1" /> Not Verified
            </Badge>
          )}
          {vehicle.flags?.isActive ? (
            <Badge bg="success" className="fs-6 px-3 py-2">
              <FaCheck className="me-1" /> Active
            </Badge>
          ) : (
            <Badge bg="danger" className="fs-6 px-3 py-2">
              <FaTimes className="me-1" /> Inactive
            </Badge>
          )}
          {vehicle.registration?.isCommercial && (
            <Badge bg="primary" className="fs-6 px-3 py-2 ms-2">
              Commercial
            </Badge>
          )}
        </Col>
      </Row>

      {/* Tabs Navigation */}
      <Row className="mb-4">
        <Col>
          <Tabs
            activeKey={activeTab}
            onSelect={(k) => setActiveTab(k)}
            className="mb-3"
          >
            <Tab eventKey="details" title="Vehicle Details">
              <Row>
                <Col lg={8}>
                  {/* Images Gallery */}
                  <Card className="mb-4">
                    <Card.Header>
                      Gallery ({vehicle.media?.images?.length || 0} images)
                    </Card.Header>
                    <Card.Body>
                      <Row>
                        {vehicle.media?.images?.map((img, index) => (
                          <Col md={4} key={index} className="mb-3">
                            <Image
                              src={img}
                              alt={`Vehicle ${index + 1}`}
                              fluid
                              rounded
                              className="vehicle-image"
                              style={{ height: "200px", objectFit: "cover" }}
                            />
                          </Col>
                        ))}
                        {(!vehicle.media?.images || vehicle.media.images.length === 0) && (
                          <Col className="text-center py-5">
                            <FaCar size={50} className="text-muted mb-3" />
                            <p className="text-muted">No images available</p>
                          </Col>
                        )}
                      </Row>
                    </Card.Body>
                  </Card>

                  {/* Basic Specifications */}
                  <Card className="mb-4">
                    <Card.Header>Basic Specifications</Card.Header>
                    <Card.Body>
                      <Row>
                        <Col md={6}>
                          <Table borderless>
                            <tbody>
                              <tr>
                                <td>
                                  <strong>Title:</strong>
                                </td>
                                <td>{vehicle.title}</td>
                              </tr>
                              <tr>
                                <td>
                                  <strong>Brand:</strong>
                                </td>
                                <td>{vehicle.brand}</td>
                              </tr>
                              <tr>
                                <td>
                                  <strong>Model:</strong>
                                </td>
                                <td>{vehicle.model}</td>
                              </tr>
                              <tr>
                                <td>
                                  <strong>Variant:</strong>
                                </td>
                                <td>{vehicle.variant || "N/A"}</td>
                              </tr>
                              <tr>
                                <td>
                                  <strong>Color:</strong>
                                </td>
                                <td>{vehicle.color || "N/A"}</td>
                              </tr>
                              <tr>
                                <td>
                                  <strong>Manufacturing Year:</strong>
                                </td>
                                <td>{vehicle.manufacturing?.year || "N/A"}</td>
                              </tr>
                              <tr>
                                <td>
                                  <strong>Manufacturing Month/Year:</strong>
                                </td>
                                <td>{vehicle.manufacturing?.monthYear || "N/A"}</td>
                              </tr>
                            </tbody>
                          </Table>
                        </Col>
                        <Col md={6}>
                          <Table borderless>
                            <tbody>
                              <tr>
                                <td>
                                  <strong>Body Type:</strong>
                                </td>
                                <td>{vehicle.identifiers?.bodyType || "N/A"}</td>
                              </tr>
                              <tr>
                                <td>
                                  <strong>Vehicle Class:</strong>
                                </td>
                                <td>{vehicle.identifiers?.class || "N/A"}</td>
                              </tr>
                              <tr>
                                <td>
                                  <strong>Chassis Number:</strong>
                                </td>
                                <td>{vehicle.identifiers?.chassis || "N/A"}</td>
                              </tr>
                              <tr>
                                <td>
                                  <strong>Engine Number:</strong>
                                </td>
                                <td>{vehicle.identifiers?.engine || "N/A"}</td>
                              </tr>
                              <tr>
                                <td>
                                  <strong>Cubic Capacity:</strong>
                                </td>
                                <td>{vehicle.specifications?.cubicCapacity || "N/A"}</td>
                              </tr>
                              <tr>
                                <td>
                                  <strong>Cylinders:</strong>
                                </td>
                                <td>{vehicle.specifications?.cylinders || "N/A"}</td>
                              </tr>
                              <tr>
                                <td>
                                  <strong>Seat Capacity:</strong>
                                </td>
                                <td>{vehicle.specifications?.seatCapacity || "N/A"}</td>
                              </tr>
                            </tbody>
                          </Table>
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>

                  {/* Registration Details */}
                  <Card className="mb-4">
                    <Card.Header>Registration & RTO Details</Card.Header>
                    <Card.Body>
                      <Row>
                        <Col md={6}>
                          <Table borderless>
                            <tbody>
                              <tr>
                                <td>
                                  <strong>Registration Number:</strong>
                                </td>
                                <td>{vehicle.registration?.number || "N/A"}</td>
                              </tr>
                              <tr>
                                <td>
                                  <strong>RTO Authority:</strong>
                                </td>
                                <td>{vehicle.registration?.authority || "N/A"}</td>
                              </tr>
                              <tr>
                                <td>
                                  <strong>Registration Date:</strong>
                                </td>
                                <td>{formatDate(vehicle.registration?.date)}</td>
                              </tr>
                              <tr>
                                <td>
                                  <strong>Expiry Date:</strong>
                                </td>
                                <td>{formatDate(vehicle.registration?.expiryDate)}</td>
                              </tr>
                              <tr>
                                <td>
                                  <strong>RTO Code:</strong>
                                </td>
                                <td>{vehicle.registration?.rtoCode || "N/A"}</td>
                              </tr>
                            </tbody>
                          </Table>
                        </Col>
                        <Col md={6}>
                          <Table borderless>
                            <tbody>
                              <tr>
                                <td>
                                  <strong>Registration Status:</strong>
                                </td>
                                <td>{vehicle.registration?.status || "N/A"}</td>
                              </tr>
                              <tr>
                                <td>
                                  <strong>Status As On:</strong>
                                </td>
                                <td>{vehicle.registration?.statusAsOn || "N/A"}</td>
                              </tr>
                              <tr>
                                <td>
                                  <strong>Norms Type:</strong>
                                </td>
                                <td>{vehicle.registration?.normsType || "N/A"}</td>
                              </tr>
                              <tr>
                                <td>
                                  <strong>Financer:</strong>
                                </td>
                                <td>{vehicle.registration?.financer || "N/A"}</td>
                              </tr>
                              <tr>
                                <td>
                                  <strong>Commercial Vehicle:</strong>
                                </td>
                                <td>{vehicle.registration?.isCommercial ? "Yes" : "No"}</td>
                              </tr>
                            </tbody>
                          </Table>
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>

                  {/* Owner Details */}
                  <Card className="mb-4">
                    <Card.Header>Owner Details</Card.Header>
                    <Card.Body>
                      <Row>
                        <Col md={6}>
                          <Table borderless>
                            <tbody>
                              <tr>
                                <td>
                                  <strong>Owner Name:</strong>
                                </td>
                                <td>{vehicle.owner?.name || "N/A"}</td>
                              </tr>
                              <tr>
                                <td>
                                  <strong>Father's Name:</strong>
                                </td>
                                <td>{vehicle.owner?.fatherName || "N/A"}</td>
                              </tr>
                              <tr>
                                <td>
                                  <strong>Mobile:</strong>
                                </td>
                                <td>{vehicle.owner?.mobile || "N/A"}</td>
                              </tr>
                              <tr>
                                <td>
                                  <strong>Owner Count:</strong>
                                </td>
                                <td>{vehicle.owner?.count || "N/A"}</td>
                              </tr>
                            </tbody>
                          </Table>
                        </Col>
                        <Col md={6}>
                          <Table borderless>
                            <tbody>
                              <tr>
                                <td>
                                  <strong>Present Address:</strong>
                                </td>
                                <td>
                                  {vehicle.addresses?.present?.full || "N/A"}
                                  <br />
                                  {vehicle.addresses?.present?.city && (
                                    <>
                                      {vehicle.addresses.present.city}, {vehicle.addresses.present.state}
                                      <br />
                                      Pincode: {vehicle.addresses.present.pincode}
                                    </>
                                  )}
                                </td>
                              </tr>
                              <tr>
                                <td>
                                  <strong>Permanent Address:</strong>
                                </td>
                                <td>
                                  {vehicle.addresses?.permanent?.full || "N/A"}
                                  <br />
                                  {vehicle.addresses?.permanent?.city && (
                                    <>
                                      {vehicle.addresses.permanent.city}, {vehicle.addresses.permanent.state}
                                      <br />
                                      Pincode: {vehicle.addresses.permanent.pincode}
                                    </>
                                  )}
                                </td>
                              </tr>
                            </tbody>
                          </Table>
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>

                  {/* EV Specifications */}
                  {vehicle.evSpecs && (
                    <Card className="mb-4">
                      <Card.Header>EV Specifications</Card.Header>
                      <Card.Body>
                        <Row>
                          <Col md={6}>
                            <Table borderless>
                              <tbody>
                                <tr>
                                  <td>
                                    <strong>Battery Capacity:</strong>
                                  </td>
                                  <td>
                                    {vehicle.evSpecs.batteryCapacity?.value}{" "}
                                    {vehicle.evSpecs.batteryCapacity?.unit}
                                  </td>
                                </tr>
                                <tr>
                                  <td>
                                    <strong>Battery Type:</strong>
                                  </td>
                                  <td>{vehicle.evSpecs.batteryType || "N/A"}</td>
                                </tr>
                                <tr>
                                  <td>
                                    <strong>Range:</strong>
                                  </td>
                                  <td>
                                    {vehicle.evSpecs.range?.value}{" "}
                                    {vehicle.evSpecs.range?.unit}
                                  </td>
                                </tr>
                                <tr>
                                  <td>
                                    <strong>Charging Type:</strong>
                                  </td>
                                  <td>{vehicle.evSpecs.charging?.chargingType || "N/A"}</td>
                                </tr>
                              </tbody>
                            </Table>
                          </Col>
                          <Col md={6}>
                            <Table borderless>
                              <tbody>
                                <tr>
                                  <td>
                                    <strong>Motor Power:</strong>
                                  </td>
                                  <td>
                                    {vehicle.evSpecs.motorPower?.value}{" "}
                                    {vehicle.evSpecs.motorPower?.unit}
                                  </td>
                                </tr>
                                <tr>
                                  <td>
                                    <strong>Max Speed:</strong>
                                  </td>
                                  <td>
                                    {vehicle.evSpecs.maxSpeed
                                      ? `${vehicle.evSpecs.maxSpeed} km/h`
                                      : "N/A"}
                                  </td>
                                </tr>
                                <tr>
                                  <td>
                                    <strong>Normal Charging Time:</strong>
                                  </td>
                                  <td>
                                    {vehicle.evSpecs.charging?.normalTime || "N/A"} hours
                                  </td>
                                </tr>
                                <tr>
                                  <td>
                                    <strong>Fast Charging Time:</strong>
                                  </td>
                                  <td>
                                    {vehicle.evSpecs.charging?.fastTime || "N/A"} hours
                                  </td>
                                </tr>
                                <tr>
                                  <td>
                                    <strong>Battery Health:</strong>
                                  </td>
                                  <td>
                                    <div className="d-flex align-items-center">
                                      <ProgressBar
                                        now={vehicle.evSpecs.batteryHealthPercent || 0}
                                        label={`${vehicle.evSpecs.batteryHealthPercent || 0}%`}
                                        variant={
                                          (vehicle.evSpecs.batteryHealthPercent || 0) >= 80
                                            ? "success"
                                            : (vehicle.evSpecs.batteryHealthPercent || 0) >= 60
                                            ? "warning"
                                            : "danger"
                                        }
                                        style={{ width: "100px" }}
                                        className="me-2"
                                      />
                                      {vehicle.evSpecs.batteryHealthPercent || 0}%
                                    </div>
                                  </td>
                                </tr>
                                <tr>
                                  <td>
                                    <strong>Charger Status:</strong>
                                  </td>
                                  <td>
                                    {vehicle.evSpecs.chargerStatus ? (
                                      <Badge bg="success">Available</Badge>
                                    ) : (
                                      <Badge bg="secondary">Not Available</Badge>
                                    )}
                                  </td>
                                </tr>
                              </tbody>
                            </Table>
                          </Col>
                        </Row>
                      </Card.Body>
                    </Card>
                  )}

                  {/* Bidders List */}
                  <Card className="mb-4">
                    <Card.Header>
                      <FaGavel className="me-2" />
                      Bidders ({allBidderCount})
                    </Card.Header>
                    <Card.Body>
                      {allBidder.length > 0 ? (
                        <div className="table-responsive">
                          <Table
                            striped
                            bordered
                            hover
                            className="align-middle"
                          >
                            <thead className="bg-light text-center">
                              <tr>
                                <th>
                                  <strong>Bidder</strong>
                                </th>
                                <th>
                                  <strong>Bid Amount</strong>
                                </th>
                                <th>
                                  <strong>Bid Time</strong>
                                </th>
                                <th>
                                  <strong>Status</strong>
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {allBidder.map((bid) => (
                                <tr key={bid._id} className="text-center">
                                  <td>
                                    <div>
                                      <FaUser className="me-1" />
                                      {bid.bidder?.userName || "Unknown"}
                                    </div>
                                    <small className="text-muted">
                                      {bid.bidder?.phone || "N/A"}
                                    </small>
                                  </td>
                                  <td>
                                    <div className="fw-bold">
                                      {formatPrice(bid.amount)}
                                    </div>
                                  </td>
                                  <td>
                                    <div>{formatDate(bid.time)}</div>
                                  </td>
                                  <td>
                                    <Badge bg="secondary">
                                      {bid.bidStatus || "Active"}
                                    </Badge>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        </div>
                      ) : (
                        <Alert variant="info" className="mb-0">
                          No bids yet for this vehicle.
                        </Alert>
                      )}
                    </Card.Body>
                  </Card>
                </Col>

                <Col lg={4}>
                  {/* Auction Information */}
                  <Card className="mb-4">
                    <Card.Header>Auction Information</Card.Header>
                    <Card.Body>
                      <Table borderless>
                        <tbody>
                          <tr>
                            <td>
                              <strong>Base Price:</strong>
                            </td>
                            <td className="text-end">
                              {formatPrice(vehicle.pricing?.basePrice)}
                            </td>
                          </tr>
                          <tr>
                            <td>
                              <strong>Reserve Price:</strong>
                            </td>
                            <td className="text-end">
                              {vehicle.pricing?.reservePrice
                                ? formatPrice(vehicle.pricing.reservePrice)
                                : "N/A"}
                            </td>
                          </tr>
                          <tr>
                            <td>
                              <strong>Current Bid:</strong>
                            </td>
                            <td className="text-end text-success fw-bold">
                              {formatPrice(vehicle.pricing?.currentBid)}
                            </td>
                          </tr>
                          <tr>
                            <td>
                              <strong>Expected Price:</strong>
                            </td>
                            <td className="text-end">
                              {vehicle.pricing?.expectedPrice || "N/A"}
                            </td>
                          </tr>
                          <tr className="border-top">
                            <td>
                              <strong>Bid Count:</strong>
                            </td>
                            <td className="text-end">{vehicle.stats?.bids || 0}</td>
                          </tr>
                          <tr>
                            <td>
                              <strong>View Count:</strong>
                            </td>
                            <td className="text-end">{vehicle.stats?.views || 0}</td>
                          </tr>
                          <tr className="border-top">
                            <td>
                              <strong>Auction Start:</strong>
                            </td>
                            <td className="text-end">
                              <FaCalendar className="me-1" />
                              {formatDate(vehicle.auction?.startTime)}
                            </td>
                          </tr>
                          <tr>
                            <td>
                              <strong>Auction End:</strong>
                            </td>
                            <td className="text-end">
                              <FaCalendar className="me-1" />
                              {formatDate(vehicle.auction?.endTime)}
                            </td>
                          </tr>
                        </tbody>
                      </Table>
                      {vehicle.auction?.winner && (
                        <Alert variant="success" className="mt-3">
                          <strong>Winner:</strong> {vehicle.auction.winner.bidder?.userName || "Unknown"}
                          <br />
                          <strong>Winning Amount:</strong> {formatPrice(vehicle.auction.winner.amount)}
                        </Alert>
                      )}
                    </Card.Body>
                  </Card>

                  {/* Insurance & Permits */}
                  <Card className="mb-4">
                    <Card.Header>
                      <FaShieldAlt className="me-2" />
                      Insurance & Permits
                    </Card.Header>
                    <Card.Body>
                      <div className="mb-3">
                        <strong>Insurance:</strong>
                        {vehicle.insurance?.company ? (
                          <div className="mt-1">
                            <small>Company: {vehicle.insurance.company}</small>
                            <br />
                            <small>Policy: {vehicle.insurance.policyNumber}</small>
                            <br />
                            <small>Valid Until: {formatDate(vehicle.insurance.validUpto)}</small>
                          </div>
                        ) : (
                          <div className="text-muted">No insurance information</div>
                        )}
                      </div>
                      <div className="mb-3">
                        <strong>PUC:</strong>
                        {vehicle.puc?.number ? (
                          <div className="mt-1">
                            <small>Number: {vehicle.puc.number}</small>
                            <br />
                            <small>Valid Until: {formatDate(vehicle.puc.validUpto)}</small>
                          </div>
                        ) : (
                          <div className="text-muted">No PUC information</div>
                        )}
                      </div>
                      <div>
                        <strong>Permits:</strong>
                        {vehicle.permits?.permitNumber ? (
                          <div className="mt-1">
                            <small>Type: {vehicle.permits.permitType}</small>
                            <br />
                            <small>Valid: {formatDate(vehicle.permits.validFrom)} to {formatDate(vehicle.permits.validUpto)}</small>
                            {vehicle.permits.nationalPermitNumber && (
                              <>
                                <br />
                                <small>National Permit: {vehicle.permits.nationalPermitNumber}</small>
                                <br />
                                <small>Valid Until: {formatDate(vehicle.permits.nationalPermitUpto)}</small>
                              </>
                            )}
                          </div>
                        ) : (
                          <div className="text-muted">No permit information</div>
                        )}
                      </div>
                    </Card.Body>
                  </Card>

                  {/* Location/Yard Information */}
                  <Card className="mb-4">
                    <Card.Header>
                      <FaLocationArrow className="me-2" />
                      Location / Yard
                    </Card.Header>
                    <Card.Body>
                      <div className="d-flex align-items-start">
                        <FaLocationArrow className="me-2 text-muted mt-1" />
                        <div>
                          <strong>{vehicle.yard?.parkedAt || "N/A"}</strong>
                          {vehicle.yard?.address && (
                            <div className="small text-muted mt-1">
                              {vehicle.yard.address}
                            </div>
                          )}
                          {vehicle.yard?.pocName && (
                            <>
                              <div className="mt-2">
                                <small>
                                  <strong>POC:</strong> {vehicle.yard.pocName}
                                </small>
                              </div>
                              <div className="small text-muted">
                                Contact: {vehicle.yard.pocContact || "N/A"}
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    </Card.Body>
                  </Card>

                  {/* Technical Specifications */}
                  <Card className="mb-4">
                    <Card.Header>
                      <FaCogs className="me-2" />
                      Technical Specifications
                    </Card.Header>
                    <Card.Body>
                      <Table borderless size="sm">
                        <tbody>
                          <tr>
                            <td>
                              <strong>Sleeper Capacity:</strong>
                            </td>
                            <td className="text-end">
                              {vehicle.specifications?.sleeperCapacity || "N/A"}
                            </td>
                          </tr>
                          <tr>
                            <td>
                              <strong>Standing Capacity:</strong>
                            </td>
                            <td className="text-end">
                              {vehicle.specifications?.standingCapacity || "N/A"}
                            </td>
                          </tr>
                          <tr>
                            <td>
                              <strong>Gross Weight:</strong>
                            </td>
                            <td className="text-end">
                              {vehicle.specifications?.grossWeight || "N/A"}
                            </td>
                          </tr>
                          <tr>
                            <td>
                              <strong>Unladen Weight:</strong>
                            </td>
                            <td className="text-end">
                              {vehicle.specifications?.unladenWeight || "N/A"}
                            </td>
                          </tr>
                          <tr>
                            <td>
                              <strong>Wheelbase:</strong>
                            </td>
                            <td className="text-end">
                              {vehicle.specifications?.wheelbase || "N/A"}
                            </td>
                          </tr>
                          <tr>
                            <td>
                              <strong>Vehicle Category:</strong>
                            </td>
                            <td className="text-end">
                              {vehicle.specifications?.category || "N/A"}
                            </td>
                          </tr>
                        </tbody>
                      </Table>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </Tab>

            <Tab eventKey="documents" title="Documents">
              <Row>
                <Col>
                  <Card>
                    <Card.Header>Vehicle Documents</Card.Header>
                    <Card.Body>
                      <Row>
                        <Col md={4} className="mb-3">
                          <Card className="h-100">
                            <Card.Body className="text-center">
                              <FaIdCard size={40} className="mb-3 text-primary" />
                              <Card.Title>RC Book</Card.Title>
                              {vehicle.documents?.rcBook ? (
                                <Button
                                  variant="outline-primary"
                                  href={vehicle.documents.rcBook}
                                  target="_blank"
                                  className="mt-2"
                                >
                                  View RC Book
                                </Button>
                              ) : (
                                <Badge bg="secondary" className="mt-2">
                                  Not Uploaded
                                </Badge>
                              )}
                            </Card.Body>
                          </Card>
                        </Col>
                        <Col md={4} className="mb-3">
                          <Card className="h-100">
                            <Card.Body className="text-center">
                              <FaShieldAlt size={40} className="mb-3 text-success" />
                              <Card.Title>Insurance Copy</Card.Title>
                              {vehicle.documents?.insuranceCopy ? (
                                <Button
                                  variant="outline-primary"
                                  href={vehicle.documents.insuranceCopy}
                                  target="_blank"
                                  className="mt-2"
                                >
                                  View Insurance
                                </Button>
                              ) : (
                                <Badge bg="secondary" className="mt-2">
                                  Not Uploaded
                                </Badge>
                              )}
                            </Card.Body>
                          </Card>
                        </Col>
                        <Col md={4} className="mb-3">
                          <Card className="h-100">
                            <Card.Body className="text-center">
                              <FaFileAlt size={40} className="mb-3 text-info" />
                              <Card.Title>Fitness Certificate</Card.Title>
                              {vehicle.documents?.fitnessCertificate ? (
                                <Button
                                  variant="outline-primary"
                                  href={vehicle.documents.fitnessCertificate}
                                  target="_blank"
                                  className="mt-2"
                                >
                                  View Certificate
                                </Button>
                              ) : (
                                <Badge bg="secondary" className="mt-2">
                                  Not Uploaded
                                </Badge>
                              )}
                            </Card.Body>
                          </Card>
                        </Col>
                        <Col md={4} className="mb-3">
                          <Card className="h-100">
                            <Card.Body className="text-center">
                              <FaMoneyBillWave size={40} className="mb-3 text-warning" />
                              <Card.Title>Tax Receipt</Card.Title>
                              {vehicle.documents?.taxReceipt ? (
                                <Button
                                  variant="outline-primary"
                                  href={vehicle.documents.taxReceipt}
                                  target="_blank"
                                  className="mt-2"
                                >
                                  View Receipt
                                </Button>
                              ) : (
                                <Badge bg="secondary" className="mt-2">
                                  Not Uploaded
                                </Badge>
                              )}
                            </Card.Body>
                          </Card>
                        </Col>
                        <Col md={4} className="mb-3">
                          <Card className="h-100">
                            <Card.Body className="text-center">
                              <FaIndustry size={40} className="mb-3 text-danger" />
                              <Card.Title>Invoice</Card.Title>
                              {vehicle.documents?.invoice ? (
                                <Button
                                  variant="outline-primary"
                                  href={vehicle.documents.invoice}
                                  target="_blank"
                                  className="mt-2"
                                >
                                  View Invoice
                                </Button>
                              ) : (
                                <Badge bg="secondary" className="mt-2">
                                  Not Uploaded
                                </Badge>
                              )}
                            </Card.Body>
                          </Card>
                        </Col>
                        <Col md={4} className="mb-3">
                          <Card className="h-100">
                            <Card.Body className="text-center">
                              <FaBatteryFull size={40} className="mb-3 text-success" />
                              <Card.Title>Battery Warranty</Card.Title>
                              {vehicle.documents?.batteryWarranty?.validTill ? (
                                <>
                                  <div className="small mb-2">
                                    Valid Till:{" "}
                                    {formatDate(
                                      vehicle.documents.batteryWarranty.validTill
                                    )}
                                  </div>
                                  <div className="small mb-2">
                                    Remaining:{" "}
                                    {
                                      vehicle.documents.batteryWarranty.remainingMonths || 0
                                    }{" "}
                                    months
                                  </div>
                                  <Badge bg="success" className="mt-2">
                                    Active
                                  </Badge>
                                </>
                              ) : (
                                <Badge bg="secondary" className="mt-2">
                                  Not Available
                                </Badge>
                              )}
                            </Card.Body>
                          </Card>
                        </Col>
                        <Col md={4} className="mb-3">
                          <Card className="h-100">
                            <Card.Body className="text-center">
                              <FaCar size={40} className="mb-3 text-info" />
                              <Card.Title>Vehicle Warranty</Card.Title>
                              {vehicle.documents?.vehicleWarranty?.validTill ? (
                                <>
                                  <div className="small mb-2">
                                    Valid Till:{" "}
                                    {formatDate(
                                      vehicle.documents.vehicleWarranty.validTill
                                    )}
                                  </div>
                                  <div className="small mb-2">
                                    Remaining:{" "}
                                    {
                                      vehicle.documents.vehicleWarranty.remainingMonths || 0
                                    }{" "}
                                    months
                                  </div>
                                  <Badge bg="success" className="mt-2">
                                    Active
                                  </Badge>
                                </>
                              ) : (
                                <Badge bg="secondary" className="mt-2">
                                  Not Available
                                </Badge>
                              )}
                            </Card.Body>
                          </Card>
                        </Col>
                        <Col md={4} className="mb-3">
                          <Card className="h-100">
                            <Card.Body className="text-center">
                              <FaTag size={40} className="mb-3 text-primary" />
                              <Card.Title>Fastag</Card.Title>
                              {vehicle.documents?.fastag?.fastagNo ? (
                                <>
                                  <div className="small mb-2">
                                    Number: {vehicle.documents.fastag.fastagNo}
                                  </div>
                                  <div className="small mb-2">
                                    Balance: ₹
                                    {vehicle.documents.fastag.balance || "0"}
                                  </div>
                                  <Badge bg="info" className="mt-2">
                                    Active
                                  </Badge>
                                </>
                              ) : (
                                <Badge bg="secondary" className="mt-2">
                                  Not Available
                                </Badge>
                              )}
                            </Card.Body>
                          </Card>
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </Tab>

            <Tab eventKey="timeline" title="Timeline">
              <Row>
                <Col>
                  <Card>
                    <Card.Header>Activity Timeline</Card.Header>
                    <Card.Body>
                      <div className="timeline">
                        <div className="timeline-item">
                          <div className="timeline-marker bg-primary"></div>
                          <div className="timeline-content">
                            <h6>Vehicle Created</h6>
                            <small className="text-muted">
                              {formatDate(vehicle.createdAt)}
                            </small>
                          </div>
                        </div>
                        {vehicle.valuationReport && (
                          <div className="timeline-item">
                            <div className="timeline-marker bg-info"></div>
                            <div className="timeline-content">
                              <h6>Valuation Report Generated</h6>
                              <small className="text-muted">
                                Report: {vehicle.valuationReport}
                              </small>
                            </div>
                          </div>
                        )}
                        <div className="timeline-item">
                          <div className="timeline-marker bg-warning"></div>
                          <div className="timeline-content">
                            <h6>Auction Created</h6>
                            <small className="text-muted">
                              Status: {vehicle.auction?.status || "Draft"}
                            </small>
                          </div>
                        </div>
                        <div className="timeline-item">
                          <div className="timeline-marker bg-success"></div>
                          <div className="timeline-content">
                            <h6>Auction Starts</h6>
                            <small className="text-muted">
                              {formatDate(vehicle.auction?.startTime)}
                            </small>
                          </div>
                        </div>
                        <div className="timeline-item">
                          <div className="timeline-marker bg-danger"></div>
                          <div className="timeline-content">
                            <h6>Auction Ends</h6>
                            <small className="text-muted">
                              {formatDate(vehicle.auction?.endTime)}
                            </small>
                          </div>
                        </div>
                        {vehicle.auction?.winner && (
                          <div className="timeline-item">
                            <div className="timeline-marker bg-success"></div>
                            <div className="timeline-content">
                              <h6>Vehicle Sold</h6>
                              <small className="text-muted">
                                Winning Bid:{" "}
                                {formatPrice(vehicle.auction.winner.amount)}
                              </small>
                              {vehicle.auction.winner.bidder?.userName && (
                                <div className="small">
                                  Winner: {vehicle.auction.winner.bidder.userName}
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                        {vehicle.updatedAt && (
                          <div className="timeline-item">
                            <div className="timeline-marker bg-secondary"></div>
                            <div className="timeline-content">
                              <h6>Last Updated</h6>
                              <small className="text-muted">
                                {formatDate(vehicle.updatedAt)}
                              </small>
                            </div>
                          </div>
                        )}
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </Tab>
          </Tabs>
        </Col>
      </Row>

      {/* Edit Modal */}
      <Modal
        show={showEditModal}
        onHide={() => setShowEditModal(false)}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Edit Vehicle</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Title</Form.Label>
              <Form.Control type="text" defaultValue={vehicle.title} />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Base Price (INR)</Form.Label>
              <Form.Control 
                type="number" 
                defaultValue={vehicle.pricing?.basePrice} 
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Reserve Price (INR)</Form.Label>
              <Form.Control 
                type="number" 
                defaultValue={vehicle.pricing?.reservePrice} 
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Expected Price</Form.Label>
              <Form.Control 
                type="text" 
                defaultValue={vehicle.pricing?.expectedPrice} 
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Is Featured</Form.Label>
              <Form.Check
                type="checkbox"
                label="Feature this vehicle"
                defaultChecked={vehicle.flags?.isFeatured}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Is Active</Form.Label>
              <Form.Check
                type="checkbox"
                label="Make vehicle active"
                defaultChecked={vehicle.flags?.isActive}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Is Verified</Form.Label>
              <Form.Check
                type="checkbox"
                label="Mark vehicle as verified"
                defaultChecked={vehicle.flags?.isVerified}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowEditModal(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              /* Handle save */
              setShowEditModal(false);
            }}
          >
            Save Changes
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Status Update Modal */}
      <Modal show={showStatusModal} onHide={() => setShowStatusModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Update Auction Status</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="d-grid gap-2">
            {["Draft", "Upcoming", "Live", "Ended", "Sold", "Unsold"].map((status) => (
              <Button
                key={status}
                variant={
                  vehicle.auction?.status === status
                    ? "primary"
                    : "outline-primary"
                }
                onClick={() => handleUpdateStatus(status)}
                className="text-start"
              >
                {status}
                {vehicle.auction?.status === status && (
                  <span className="float-end">✓ Current</span>
                )}
              </Button>
            ))}
          </div>
        </Modal.Body>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Deactivation</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to deactivate this vehicle?
          <Alert variant="warning" className="mt-3">
            <strong>Note:</strong> This will mark the vehicle as inactive instead of permanent deletion.
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDeleteVehicle}>
            <FaTrash className="me-1" /> Deactivate Vehicle
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default VehicleDetailsPage;